import { useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    CheckCircle2, XCircle, Pencil, AlertTriangle, Sparkles, Send,
    Paperclip, Loader2, Building2, ClipboardList, Users, Link as LinkIcon,
    HelpCircle,
} from "lucide-react";

/**
 * Sprint B — CEO Smart Brief Builder
 *
 * Reusable CEO-driven intake panel that powers ClientForm, ProjectNew,
 * and the Competitor sub-panel on ProjectBoard. Sends a paste +
 * uploaded files to POST /api/intake/extract, then surfaces:
 *   • Client fields            (FieldChip rows + Confirm / Edit / Reject)
 *   • Project fields            (same)
 *   • Competitors[]             (per-row table: Confirm / Edit / Remove / Mark unverified)
 *   • Social links[]            (per-row table)
 *   • Missing questions[]       (CEO prompts to fill the gaps)
 *
 * NOTHING is persisted until the user clicks Apply, which:
 *   • Optionally calls POST /api/intake/apply to write confirmed
 *     competitor / social rows directly into the source-of-truth
 *     systems (when target_client_id / target_project_id is known), AND
 *   • Forwards every confirmed (client + project) field to the parent
 *     via onApply(draft, confirmed) — the parent decides how to fill
 *     its own form (ClientForm / ProjectNew never auto-save).
 *
 * Strict scope: this component does NOT run the pipeline, does NOT
 * scrape social platforms, does NOT post anywhere, does NOT touch
 * OAuth, video gen, pricing, or payments.
 */

const SOURCE_LABELS = {
    user_provided: "User-provided",
    extracted_from_paste: "From paste",
    website_verified: "Website-verified",
    ai_inference: "AI inference",
    needs_confirmation: "Needs confirmation",
    // Legacy fall-backs in case an old draft round-trips here.
    client_provided: "User-provided",
    asset_extracted: "From paste",
    ai_inferred: "AI inference",
};

const SOURCE_STYLES = {
    user_provided: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    extracted_from_paste: "bg-sky-500/10 text-sky-300 border-sky-500/30",
    website_verified: "bg-teal-500/10 text-teal-300 border-teal-500/30",
    ai_inference: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    needs_confirmation: "bg-rose-500/10 text-rose-300 border-rose-500/30",
    client_provided: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    asset_extracted: "bg-sky-500/10 text-sky-300 border-sky-500/30",
    ai_inferred: "bg-amber-500/10 text-amber-300 border-amber-500/30",
};

const CONTEXT_DESCRIPTIONS = {
    client: "Building a CLIENT — extract identity, brand story, social links, and competitors.",
    project: "Building a PROJECT — extract campaign goal, offer, CTA, mood, visual direction, and competitor context.",
    competitor: "Adding COMPETITORS — extract individual competitor entries (name, URLs, positioning).",
    mixed: "Mixed paste — separate client identity, project brief, competitors, and social links.",
};

const PLATFORM_LABELS = {
    instagram: "Instagram",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    youtube: "YouTube",
    x_twitter: "X / Twitter",
    google_business_profile: "Google Business",
};

function SectionHeader({ icon: Icon, title, hint, badge }) {
    return (
        <div className="flex items-end justify-between mb-2.5">
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-orange-300" />
                <div className="label-micro">{title}</div>
            </div>
            <div className="flex items-center gap-2">
                {hint && <span className="text-[10px] text-zinc-600 hidden sm:inline">{hint}</span>}
                {badge}
            </div>
        </div>
    );
}

function SourcePill({ source, confidence }) {
    const pct = Math.round((confidence ?? 0.5) * 100);
    const cls = SOURCE_STYLES[source] || "bg-zinc-700/30 text-zinc-300 border-zinc-600/40";
    return (
        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${cls}`}>
            {SOURCE_LABELS[source] || source || "—"} · {pct}%
        </span>
    );
}

/** A single client/project field row with Confirm / Edit / Reject. */
function FieldRow({ path, label, value, confidence, source, status, onUpdate, testIdPrefix }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    if (value === "" || value === null || value === undefined) return null;

    const apply = () => {
        onUpdate({ value: draft, status: "confirmed" });
        setEditing(false);
    };

    const inferred = source === "ai_inference" || source === "ai_inferred"
        || source === "needs_confirmation";
    const showConfirm = status !== "confirmed";
    const isRejected = status === "rejected";

    return (
        <div
            data-testid={`${testIdPrefix}-${path}`}
            className={`border rounded-sm p-2.5 transition-colors ${
                status === "confirmed"
                    ? "border-emerald-500/40 bg-emerald-500/5"
                    : isRejected
                        ? "border-zinc-800 bg-zinc-950/50 opacity-50"
                        : inferred
                            ? "border-amber-500/40 bg-amber-500/5"
                            : "border-zinc-800 bg-zinc-950/50"
            }`}
        >
            <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
                <SourcePill source={source} confidence={confidence} />
            </div>
            {editing ? (
                <div className="flex gap-1.5">
                    <textarea
                        data-testid={`${testIdPrefix}-${path}-input`}
                        value={String(draft || "")}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={2}
                        className="flex-1 bg-black border border-zinc-700 rounded-sm px-2 py-1 text-sm text-zinc-200 outline-none focus:border-zinc-500 resize-y"
                    />
                    <button
                        data-testid={`${testIdPrefix}-${path}-save`}
                        onClick={apply}
                        className="text-[10px] uppercase tracking-wider bg-orange-500/20 border border-orange-500/40 text-orange-200 px-2 py-1 rounded-sm hover:bg-orange-500/30"
                    >Save</button>
                </div>
            ) : (
                <div className="flex items-start justify-between gap-2">
                    <div className="text-sm text-zinc-200 break-words leading-snug flex-1">
                        {String(value)}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        {showConfirm && (
                            <button
                                data-testid={`${testIdPrefix}-${path}-confirm`}
                                onClick={() => onUpdate({ status: "confirmed" })}
                                title="Confirm — apply this value"
                                className="p-1 text-zinc-500 hover:text-emerald-400"
                            >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                            </button>
                        )}
                        <button
                            data-testid={`${testIdPrefix}-${path}-edit`}
                            onClick={() => setEditing(true)}
                            title="Edit value"
                            className="p-1 text-zinc-500 hover:text-orange-300"
                        >
                            <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                            data-testid={`${testIdPrefix}-${path}-reject`}
                            onClick={() => onUpdate({ status: "rejected" })}
                            title="Reject — do not apply"
                            className="p-1 text-zinc-500 hover:text-rose-400"
                        >
                            <XCircle className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function CompetitorRow({ idx, row, status, onUpdate, onRemove }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(row);

    const inferred = row.source_label === "ai_inference" || row.source_label === "needs_confirmation";
    const isRejected = status === "rejected";

    const save = () => {
        onUpdate(draft);
        setEditing(false);
    };

    return (
        <tr
            data-testid={`comp-row-${idx}`}
            className={`border-b border-zinc-900 last:border-b-0 ${
                status === "confirmed"
                    ? "bg-emerald-500/5"
                    : isRejected
                        ? "opacity-50"
                        : inferred
                            ? "bg-amber-500/5"
                            : ""
            }`}
        >
            <td className="py-2 px-2 align-top">
                {editing ? (
                    <input
                        data-testid={`comp-${idx}-name`}
                        value={draft.name || ""}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        className="w-full bg-black border border-zinc-700 rounded-sm px-2 py-1 text-sm"
                    />
                ) : (
                    <span className="text-sm font-medium text-zinc-100">{row.name}</span>
                )}
                <div className="mt-1">
                    <SourcePill source={row.source_label} confidence={row.confidence} />
                </div>
            </td>
            <td className="py-2 px-2 align-top text-xs text-zinc-400">
                {editing ? (
                    <input
                        data-testid={`comp-${idx}-website`}
                        value={draft.website || ""}
                        onChange={(e) => setDraft({ ...draft, website: e.target.value })}
                        placeholder="https://…"
                        className="w-full bg-black border border-zinc-700 rounded-sm px-2 py-1"
                    />
                ) : (
                    row.website || "—"
                )}
            </td>
            <td className="py-2 px-2 align-top text-xs text-zinc-400">
                {editing ? (
                    <input
                        data-testid={`comp-${idx}-geo`}
                        value={draft.geography || ""}
                        onChange={(e) => setDraft({ ...draft, geography: e.target.value })}
                        className="w-full bg-black border border-zinc-700 rounded-sm px-2 py-1"
                    />
                ) : (
                    row.geography || "—"
                )}
            </td>
            <td className="py-2 px-2 align-top text-xs text-zinc-400">
                {editing ? (
                    <textarea
                        data-testid={`comp-${idx}-positioning`}
                        rows={2}
                        value={draft.positioning || ""}
                        onChange={(e) => setDraft({ ...draft, positioning: e.target.value })}
                        className="w-full bg-black border border-zinc-700 rounded-sm px-2 py-1"
                    />
                ) : (
                    <span className="line-clamp-2">{row.positioning || "—"}</span>
                )}
            </td>
            <td className="py-2 px-2 align-top whitespace-nowrap">
                <div className="flex items-center gap-1 justify-end">
                    {editing ? (
                        <button
                            data-testid={`comp-${idx}-save`}
                            onClick={save}
                            className="text-[10px] uppercase tracking-wider bg-orange-500/20 border border-orange-500/40 text-orange-200 px-2 py-1 rounded-sm hover:bg-orange-500/30"
                        >Save</button>
                    ) : (
                        <>
                            {status !== "confirmed" && (
                                <button
                                    data-testid={`comp-${idx}-confirm`}
                                    onClick={() => onUpdate({ ...row, _status: "confirmed" })}
                                    title="Confirm row"
                                    className="p-1 text-zinc-500 hover:text-emerald-400"
                                >
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                </button>
                            )}
                            <button
                                data-testid={`comp-${idx}-edit`}
                                onClick={() => setEditing(true)}
                                title="Edit row"
                                className="p-1 text-zinc-500 hover:text-orange-300"
                            >
                                <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                                data-testid={`comp-${idx}-unverify`}
                                onClick={() => onUpdate({ ...row, verification_status: "needs_confirmation" })}
                                title="Mark unverified"
                                className="p-1 text-zinc-500 hover:text-amber-300"
                            >
                                <AlertTriangle className="w-3.5 h-3.5" />
                            </button>
                            <button
                                data-testid={`comp-${idx}-remove`}
                                onClick={onRemove}
                                title="Remove row"
                                className="p-1 text-zinc-500 hover:text-rose-400"
                            >
                                <XCircle className="w-3.5 h-3.5" />
                            </button>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
}

function SocialRow({ idx, row, status, onUpdate, onRemove }) {
    const isRejected = status === "rejected";
    const inferred = row.source_label === "ai_inference" || row.source_label === "needs_confirmation";
    return (
        <tr
            data-testid={`social-row-${idx}`}
            className={`border-b border-zinc-900 last:border-b-0 ${
                status === "confirmed"
                    ? "bg-emerald-500/5"
                    : isRejected
                        ? "opacity-50"
                        : inferred
                            ? "bg-amber-500/5"
                            : ""
            }`}
        >
            <td className="py-2 px-2 align-top text-xs uppercase tracking-wider text-zinc-500">
                {row.entity_type || "client"}
            </td>
            <td className="py-2 px-2 align-top text-xs text-zinc-300 max-w-[140px] truncate">
                {row.entity_name || "—"}
            </td>
            <td className="py-2 px-2 align-top text-xs text-zinc-300">
                {PLATFORM_LABELS[row.platform] || row.platform || "—"}
            </td>
            <td className="py-2 px-2 align-top text-xs">
                <a
                    href={row.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-orange-300 underline underline-offset-2 break-all"
                >
                    {row.url}
                </a>
                <div className="mt-1"><SourcePill source={row.source_label} confidence={row.confidence} /></div>
            </td>
            <td className="py-2 px-2 align-top whitespace-nowrap">
                <div className="flex items-center gap-1 justify-end">
                    {status !== "confirmed" && (
                        <button
                            data-testid={`social-${idx}-confirm`}
                            onClick={() => onUpdate({ ...row, _status: "confirmed" })}
                            title="Confirm link"
                            className="p-1 text-zinc-500 hover:text-emerald-400"
                        >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                        </button>
                    )}
                    <button
                        data-testid={`social-${idx}-unverify`}
                        onClick={() => onUpdate({ ...row, verification_status: "needs_confirmation" })}
                        title="Mark unverified"
                        className="p-1 text-zinc-500 hover:text-amber-300"
                    >
                        <AlertTriangle className="w-3.5 h-3.5" />
                    </button>
                    <button
                        data-testid={`social-${idx}-remove`}
                        onClick={onRemove}
                        title="Remove link"
                        className="p-1 text-zinc-500 hover:text-rose-400"
                    >
                        <XCircle className="w-3.5 h-3.5" />
                    </button>
                </div>
            </td>
        </tr>
    );
}

export default function SmartBriefBuilder({
    context = "mixed",
    onApply,
    targetClientId = null,
    targetProjectId = null,
    title,
    placeholder,
    sectionVisibility,  // optional override: {client, project, competitors, social, missing}
}) {
    const [text, setText] = useState("");
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [applying, setApplying] = useState(false);
    const [draft, setDraft] = useState(null);
    // Per-row decisions: clientStatus[fieldPath] / projectStatus[fieldPath]
    // = "confirmed" | "rejected" | undefined (pending). Competitors +
    // social links keep their own arrays alongside the draft so we can
    // edit values in place.
    const [clientFields, setClientFields] = useState({});
    const [projectFields, setProjectFields] = useState({});
    const [clientStatus, setClientStatus] = useState({});
    const [projectStatus, setProjectStatus] = useState({});
    const [competitors, setCompetitors] = useState([]);
    const [competitorStatus, setCompetitorStatus] = useState({}); // idx -> "confirmed"|"rejected"
    const [socialLinks, setSocialLinks] = useState([]);
    const [socialStatus, setSocialStatus] = useState({});
    const [missingQuestions, setMissingQuestions] = useState([]);
    const [history, setHistory] = useState([
        { role: "agent", text: "Hi — I'm your CEO Intake Agent. Paste website/company/LinkedIn/competitor analysis, founder story, or campaign notes here." },
    ]);
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef(null);

    const visible = {
        client: true, project: true, competitors: true,
        social: true, missing: true,
        ...(sectionVisibility || {}),
    };
    if (context === "client") { visible.project = false; }
    if (context === "project") { visible.client = false; }
    if (context === "competitor") {
        visible.client = false;
        visible.project = false;
    }

    const ctxBadge = (
        <span className="text-[10px] uppercase tracking-wider text-zinc-500 border border-zinc-800 px-2 py-1 rounded-sm">
            {context} intake · no pipeline calls
        </span>
    );

    const onPickFiles = (incoming) => {
        const list = Array.from(incoming || []).slice(0, 8);
        setFiles((prev) => [...prev, ...list].slice(0, 8));
    };
    const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

    const send = async () => {
        if (!text.trim() && files.length === 0) {
            toast.error("Paste text or drop a file first");
            return;
        }
        setBusy(true);
        const userMsg = text.trim() || `(Sent ${files.length} file${files.length > 1 ? "s" : ""})`;
        setHistory((h) => [...h, { role: "user", text: userMsg, files: files.map((f) => f.name) }]);
        try {
            const fd = new FormData();
            fd.append("text", text);
            fd.append("context", context);
            files.forEach((f) => fd.append("files", f));
            const { data } = await api.post("/intake/extract", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 180000,
            });
            setDraft(data);
            setClientFields(data.client || {});
            setProjectFields(data.project || {});
            setClientStatus({});
            setProjectStatus({});
            setCompetitors(data.competitors || []);
            setCompetitorStatus({});
            setSocialLinks(data.social_links || []);
            setSocialStatus({});
            setMissingQuestions(data.missing_questions || []);
            setHistory((h) => [...h, {
                role: "agent",
                text: data.draft_message || "Here's what I picked up — please confirm or edit below."
            }]);
            setText("");
            setFiles([]);
        } catch (e) {
            if (e?.response?.status !== 402) {
                toast.error(e?.response?.data?.detail || e?.userMessage || "Intake failed — please retry");
            }
            setHistory((h) => [...h, { role: "agent", text: "Hmm, that didn't go through. Try again or fill the form below manually." }]);
        } finally {
            setBusy(false);
        }
    };

    // Build the per-path confirmed map that the parent (and apply
    // endpoint) consume.
    const confirmedPaths = useMemo(() => {
        const map = {};
        Object.entries(clientStatus).forEach(([k, v]) => {
            if (v === "confirmed") map[`client.${k}`] = true;
        });
        Object.entries(projectStatus).forEach(([k, v]) => {
            if (v === "confirmed") map[`project.${k}`] = true;
        });
        Object.entries(competitorStatus).forEach(([idx, v]) => {
            if (v === "confirmed") map[`competitor.${idx}`] = true;
        });
        Object.entries(socialStatus).forEach(([idx, v]) => {
            if (v === "confirmed") map[`social.${idx}`] = true;
        });
        return map;
    }, [clientStatus, projectStatus, competitorStatus, socialStatus]);

    const apply = async () => {
        if (!draft) return;
        // 1) Apply confirmed competitor/social rows server-side when we
        //    have a target project/client.
        const haveServerRows =
            (Object.values(competitorStatus).some((s) => s === "confirmed")
              && targetProjectId)
            || Object.values(socialStatus).some((s) => s === "confirmed");
        if (haveServerRows) {
            setApplying(true);
            try {
                await api.post("/intake/apply", {
                    target_client_id: targetClientId || null,
                    target_project_id: targetProjectId || null,
                    client: clientFields,
                    project: projectFields,
                    competitors,
                    social_links: socialLinks,
                    confirmed: confirmedPaths,
                });
                toast.success("Confirmed intake saved.");
            } catch (e) {
                if (e?.response?.status !== 402) {
                    toast.error(e?.userMessage || "Apply failed");
                }
                setApplying(false);
                return;
            }
            setApplying(false);
        }
        // 2) Hand off the confirmed client/project fields to the parent
        //    so it can pre-fill its own form.
        const confirmedDraft = {
            ...draft,
            client: filterConfirmed(clientFields, clientStatus),
            project: filterConfirmed(projectFields, projectStatus),
            competitors: competitors.filter((_, idx) => competitorStatus[idx] === "confirmed"),
            social_links: socialLinks.filter((_, idx) => socialStatus[idx] === "confirmed"),
        };
        onApply?.(confirmedDraft, confirmedPaths);
        toast.success("Draft applied. Review the form below before saving.");
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer?.files?.length) onPickFiles(e.dataTransfer.files);
    };

    const updateClientField = (path, patch) => {
        if (patch.value !== undefined) {
            setClientFields((f) => ({ ...f, [path]: patch.value }));
        }
        if (patch.status !== undefined) {
            setClientStatus((s) => ({ ...s, [path]: patch.status }));
        }
    };
    const updateProjectField = (path, patch) => {
        if (patch.value !== undefined) {
            setProjectFields((f) => ({ ...f, [path]: patch.value }));
        }
        if (patch.status !== undefined) {
            setProjectStatus((s) => ({ ...s, [path]: patch.status }));
        }
    };

    const confirmCount = Object.values({
        ...clientStatus, ...projectStatus, ...competitorStatus, ...socialStatus,
    }).filter((v) => v === "confirmed").length;

    return (
        <div data-testid="smart-brief-builder" className="card-base p-5 mb-6 ceo-glow">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                    <div className="label-micro mb-1">CEO Smart Brief Builder</div>
                    <h2 className="font-display text-xl font-medium tracking-tight">
                        {title || "Paste anything. I'll structure it for you."}
                    </h2>
                    <p className="text-xs text-zinc-500 mt-1">
                        {CONTEXT_DESCRIPTIONS[context] || CONTEXT_DESCRIPTIONS.mixed}
                    </p>
                </div>
                {ctxBadge}
            </div>

            {/* Chat history */}
            <div className="space-y-2 mb-4 max-h-56 overflow-y-auto pr-1" data-testid="brief-history">
                {history.map((m, i) => (
                    <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                        <div className={`max-w-[85%] px-3 py-2 rounded-sm text-sm leading-snug ${
                            m.role === "user"
                                ? "bg-orange-500/15 border border-orange-500/30 text-orange-100"
                                : "bg-zinc-900 border border-zinc-800 text-zinc-200"
                        }`}>
                            {m.text}
                            {m.files?.length > 0 && (
                                <div className="mt-1 text-[10px] text-zinc-400">
                                    <Paperclip className="inline w-3 h-3 -mt-px" /> {m.files.join(", ")}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {busy && (
                    <div className="flex justify-start">
                        <div className="px-3 py-2 rounded-sm text-sm bg-zinc-900 border border-zinc-800 text-zinc-400 flex items-center gap-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Reading your paste…
                        </div>
                    </div>
                )}
            </div>

            {/* Input + drop zone */}
            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`border ${dragOver ? "border-orange-500/60 bg-orange-500/5" : "border-zinc-800"} rounded-sm p-3 transition-colors`}
            >
                <textarea
                    data-testid="brief-text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={3}
                    placeholder={placeholder ||
                        "Paste website/company/LinkedIn/competitor analysis, founder story, or campaign notes here."}
                    className="w-full bg-transparent border-0 outline-none resize-y text-sm text-zinc-100 placeholder:text-zinc-600"
                />
                {files.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                        {files.map((f, i) => (
                            <span key={i} data-testid={`brief-file-${i}`}
                                className="inline-flex items-center gap-2 text-xs bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1">
                                <Paperclip className="w-3 h-3" /> {f.name}
                                <button onClick={() => removeFile(i)} className="text-zinc-500 hover:text-orange-400">×</button>
                            </span>
                        ))}
                    </div>
                )}
                <div className="flex items-center justify-between mt-3 gap-3">
                    <div className="flex items-center gap-2">
                        <input
                            ref={inputRef}
                            type="file"
                            multiple
                            accept="image/*,.pdf"
                            onChange={(e) => onPickFiles(e.target.files)}
                            className="hidden"
                            data-testid="brief-file-input"
                        />
                        <button
                            data-testid="brief-attach"
                            onClick={() => inputRef.current?.click()}
                            className="text-xs text-zinc-400 hover:text-zinc-100 border border-zinc-800 hover:border-zinc-600 px-2 py-1 rounded-sm">
                            <Paperclip className="inline w-3 h-3 mr-1 -mt-px" /> Attach files
                        </button>
                        <span className="text-[10px] text-zinc-600">Logo · PDF · references · up to 8 files</span>
                    </div>
                    <button
                        data-testid="brief-send"
                        onClick={send}
                        disabled={busy}
                        className="btn-primary text-sm">
                        {busy ? "Reading…" : "Send to CEO"}
                        <Send className="w-3.5 h-3.5 ml-1" />
                    </button>
                </div>
            </div>

            {/* Draft preview */}
            {draft && (
                <div className="mt-5 space-y-5" data-testid="brief-draft">
                    {/* Palette */}
                    {draft.palette?.length > 0 && (
                        <div>
                            <div className="label-micro mb-2">Suggested brand palette</div>
                            <div className="flex gap-2 flex-wrap">
                                {draft.palette.slice(0, 6).map((hex, i) => (
                                    <div key={i} className="flex items-center gap-2 border border-zinc-800 rounded-sm px-2 py-1 text-xs">
                                        <span className="inline-block w-4 h-4 rounded-sm border border-zinc-700" style={{ background: hex }} />
                                        <span className="font-mono text-zinc-300">{hex}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Detected assets */}
                    {draft.detected_assets?.length > 0 && (
                        <div>
                            <div className="label-micro mb-2">Detected assets</div>
                            <div className="flex flex-wrap gap-2">
                                {draft.detected_assets.map((a) => (
                                    <span key={a.id} className="text-xs border border-zinc-800 rounded-sm px-2 py-1 bg-zinc-950/50">
                                        <Paperclip className="inline w-3 h-3 mr-1 -mt-px" /> {a.filename}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Client fields */}
                    {visible.client && Object.keys(clientFields || {}).filter((k) => clientFields[k]).length > 0 && (
                        <div data-testid="brief-client-section">
                            <SectionHeader
                                icon={Building2}
                                title="Client fields"
                                hint="Confirm / Edit / Reject — only confirmed values are applied"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {Object.entries(clientFields)
                                    .filter(([, v]) => v !== "" && v !== null && v !== undefined)
                                    .map(([k, v]) => (
                                        <FieldRow
                                            key={k}
                                            path={k}
                                            label={k.replace(/_/g, " ")}
                                            value={v}
                                            confidence={draft.confidence?.[`client.${k}`]}
                                            source={draft.sources?.[`client.${k}`]}
                                            status={clientStatus[k]}
                                            onUpdate={(p) => updateClientField(k, p)}
                                            testIdPrefix="brief-client"
                                        />
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* Project fields */}
                    {visible.project && Object.keys(projectFields || {}).filter((k) => projectFields[k]).length > 0 && (
                        <div data-testid="brief-project-section">
                            <SectionHeader
                                icon={ClipboardList}
                                title="Project fields"
                                hint="Confirm / Edit / Reject — only confirmed values are applied"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {Object.entries(projectFields)
                                    .filter(([, v]) => v !== "" && v !== null && v !== undefined)
                                    .map(([k, v]) => (
                                        <FieldRow
                                            key={k}
                                            path={k}
                                            label={k.replace(/_/g, " ")}
                                            value={v}
                                            confidence={draft.confidence?.[`project.${k}`]}
                                            source={draft.sources?.[`project.${k}`]}
                                            status={projectStatus[k]}
                                            onUpdate={(p) => updateProjectField(k, p)}
                                            testIdPrefix="brief-project"
                                        />
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* Competitors */}
                    {visible.competitors && competitors.length > 0 && (
                        <div data-testid="brief-competitors-section">
                            <SectionHeader
                                icon={Users}
                                title={`Competitors (${competitors.length})`}
                                hint="Each row saves to Competitor Profiles + Competitor Seeds when confirmed"
                            />
                            <div className="border border-zinc-800 rounded-sm overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-500">
                                            <th className="text-left py-2 px-2">Name</th>
                                            <th className="text-left py-2 px-2">Website</th>
                                            <th className="text-left py-2 px-2">Geography</th>
                                            <th className="text-left py-2 px-2">Positioning</th>
                                            <th className="text-right py-2 px-2">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {competitors.map((row, idx) => (
                                            <CompetitorRow
                                                key={idx}
                                                idx={idx}
                                                row={row}
                                                status={competitorStatus[idx]}
                                                onUpdate={(next) => {
                                                    if (next._status) {
                                                        setCompetitorStatus((s) => ({ ...s, [idx]: next._status }));
                                                        const { _status, ...rest } = next;  // eslint-disable-line no-unused-vars
                                                        setCompetitors((arr) => arr.map((r, i) => (i === idx ? rest : r)));
                                                    } else {
                                                        setCompetitors((arr) => arr.map((r, i) => (i === idx ? next : r)));
                                                    }
                                                }}
                                                onRemove={() => {
                                                    setCompetitors((arr) => arr.filter((_, i) => i !== idx));
                                                    setCompetitorStatus((s) => {
                                                        const next = { ...s }; delete next[idx]; return next;
                                                    });
                                                }}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {!targetProjectId && (
                                <div className="text-[10px] text-amber-300/80 mt-1">
                                    No project selected — confirmed competitors will be forwarded to the parent form, but not saved to /competitor-profiles until you create a project.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Social links */}
                    {visible.social && socialLinks.length > 0 && (
                        <div data-testid="brief-social-section">
                            <SectionHeader
                                icon={LinkIcon}
                                title={`Social links (${socialLinks.length})`}
                                hint="Confirmed client links sync to social_profiles registry"
                            />
                            <div className="border border-zinc-800 rounded-sm overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-500">
                                            <th className="text-left py-2 px-2">Type</th>
                                            <th className="text-left py-2 px-2">Entity</th>
                                            <th className="text-left py-2 px-2">Platform</th>
                                            <th className="text-left py-2 px-2">URL</th>
                                            <th className="text-right py-2 px-2">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {socialLinks.map((row, idx) => (
                                            <SocialRow
                                                key={idx}
                                                idx={idx}
                                                row={row}
                                                status={socialStatus[idx]}
                                                onUpdate={(next) => {
                                                    if (next._status) {
                                                        setSocialStatus((s) => ({ ...s, [idx]: next._status }));
                                                        const { _status, ...rest } = next;  // eslint-disable-line no-unused-vars
                                                        setSocialLinks((arr) => arr.map((r, i) => (i === idx ? rest : r)));
                                                    } else {
                                                        setSocialLinks((arr) => arr.map((r, i) => (i === idx ? next : r)));
                                                    }
                                                }}
                                                onRemove={() => {
                                                    setSocialLinks((arr) => arr.filter((_, i) => i !== idx));
                                                    setSocialStatus((s) => {
                                                        const next = { ...s }; delete next[idx]; return next;
                                                    });
                                                }}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Missing questions */}
                    {visible.missing && missingQuestions.length > 0 && (
                        <div data-testid="brief-missing-section">
                            <SectionHeader
                                icon={HelpCircle}
                                title="Still missing"
                                hint="3-6 high-impact questions to answer next"
                            />
                            <ul className="space-y-1.5">
                                {missingQuestions.map((m, i) => (
                                    <li
                                        key={i}
                                        data-testid={`brief-missing-${i}`}
                                        className={`text-xs border rounded-sm px-2.5 py-1.5 ${
                                            m.priority === "high"
                                                ? "border-rose-500/30 bg-rose-500/5 text-rose-100"
                                                : m.priority === "medium"
                                                    ? "border-amber-500/30 bg-amber-500/5 text-amber-100"
                                                    : "border-zinc-700 bg-zinc-900 text-zinc-300"
                                        }`}
                                    >
                                        <span className="font-medium">{m.question}</span>
                                        {m.why && <span className="text-zinc-500"> — {m.why}</span>}
                                        {m.field && (
                                            <span className="ml-1 text-[10px] uppercase tracking-wider text-zinc-600">[{m.field}]</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Apply bar */}
                    <div className="flex items-center justify-between gap-2 pt-3 border-t border-zinc-900">
                        <span className="text-xs text-zinc-500">
                            {confirmCount === 0
                                ? "Confirm at least one row before applying."
                                : `${confirmCount} confirmed`}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                data-testid="brief-discard"
                                onClick={() => setDraft(null)}
                                className="text-xs text-zinc-400 hover:text-zinc-100 px-3 py-1.5">
                                Discard draft
                            </button>
                            <button
                                data-testid="brief-apply"
                                onClick={apply}
                                disabled={applying || confirmCount === 0}
                                className="btn-primary text-sm disabled:opacity-50">
                                {applying ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Applying…</>)
                                    : (<><Sparkles className="w-3.5 h-3.5" /> Apply confirmed</>)}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function filterConfirmed(values, status) {
    const out = {};
    Object.entries(values || {}).forEach(([k, v]) => {
        if (status?.[k] === "confirmed") out[k] = v;
    });
    return out;
}
